// Puts the resolved theme on screen.
//
// Replaces the old useTenantTheme, which set three raw colour variables and
// knew nothing about light or dark. Branding still arrives from the tenant
// config; it is now folded into a full token set by core, contrast-corrected
// for whichever scheme is in force, instead of being trusted as-is.

import { useEffect } from 'react';
import { resolveTheme, type TenantBranding } from '@invoiceflow/portal-core';

import { applyFontFamily, applyTheme, onSystemSchemeChange } from '../services/theme';
import { useEffectiveScheme, useThemeStore } from '../stores/useThemeStore';

/**
 * Reads the stored preference once and keeps the store in step with the OS.
 * Mount this once, above anything that renders.
 */
export function useThemeBootstrap(): void {
  const hydrate = useThemeStore(state => state.hydrate);
  const setSystemScheme = useThemeStore(state => state.setSystemScheme);

  useEffect(() => {
    void hydrate();
    return onSystemSchemeChange(setSystemScheme);
  }, [hydrate, setSystemScheme]);
}

/**
 * Applies tenant branding under the active scheme.
 *
 * Called from the layout (signed in, tenant known) and from the login screen
 * (tenant known from the URL). Both are safe: the token set is complete with or
 * without branding, so an unbranded screen is the default palette rather than
 * an unstyled one.
 */
export function useAppliedTheme(branding: TenantBranding | undefined): void {
  const scheme = useEffectiveScheme();

  useEffect(() => {
    applyTheme(resolveTheme({ scheme, branding }), scheme);
  }, [scheme, branding?.primaryColor, branding?.accentColor]);

  useEffect(() => {
    applyFontFamily(branding?.fontFamily);
  }, [branding?.fontFamily]);
}
